import { Component, OnInit } from '@angular/core';
import { ServiceOrdersService } from '../../../services/service-orders.service';
import { ConfigOrdersService, ConfigOrder as ConfigOrderDTO } from '../../../services/config-orders.service';
import { StatusServiceOrdersService, StatusServiceOrder } from '../../../services/status-service-orders.service';
import { DepartmentService } from '../../../services/department.service';
import { AuthService } from '../../../services/auth.service';
import {
  filterAccessibleConfigOrders,
  mergeAccessibleConfigOrderIds
} from '../../../utils/config-order-access.util';
import { ServiceOrder } from '../../../models/ServiceOrder';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const MAX_ORDERS_PER_CONFIG = 100;

@Component({
  selector: 'app-service-orders-consulta',
  templateUrl: './service-orders-consulta.component.html',
  styleUrls: ['./service-orders-consulta.component.css']
})
export class ServiceOrdersConsultaComponent implements OnInit {
  serviceOrders: ServiceOrder[] = [];
  loading = false;
  error = '';
  hasSearched = false;

  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  selectedStatus = '';
  filterOrderNumber = '';
  filterPlate = '';
  filterStartDate = '';
  filterEndDate = '';
  selectedConfigOrderId: number | null = null;
  filterB2bContractId = '';
  filterIsScheduled: '' | 'true' | 'false' = '';

  statusOptions: StatusServiceOrder[] = [];
  configOrderOptions: ConfigOrderDTO[] = [];
  allowedConfigOrderIds: number[] = [];

  showViewModal = false;
  selectedServiceOrderId: number | null = null;

  private isClientPaginated = false;
  private cachedAllOrders: ServiceOrder[] = [];
  private departmentNameCache = new Map<number, string>();

  tableColumns = [
    { label: 'numeroOS', titulo: 'Nº OS' },
    { label: 'motivoAbertura', titulo: 'Motivo Abertura', formatter: (value: any) => {
      const trimmed = (value ?? '').toString().trim();
      return trimmed || '—';
    }},
    {
      label: 'status',
      titulo: 'Status',
      type: 'chip',
      customClass: (value: any) => this.getStatusCellClass(value)
    },
    { label: 'dataAbertura', titulo: 'Data Abertura', type: 'date' },
    { label: 'veiculoPlaca', titulo: 'Veículo' },
    { label: 'setorAtual', titulo: 'Setor Atual' }
  ];

  tableActions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-info',
      visible: true
    }
  ];

  constructor(
    private serviceOrdersService: ServiceOrdersService,
    private configOrdersService: ConfigOrdersService,
    private statusServiceOrdersService: StatusServiceOrdersService,
    private departmentService: DepartmentService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.allowedConfigOrderIds = this.authService.getAllowedConfigOrderIdsFromToken();
    this.loadConfigOrders();
  }

  loadConfigOrders(): void {
    this.configOrdersService.getAll().subscribe({
      next: (data) => {
        const tokenIds = this.authService.getAllowedConfigOrderIdsFromToken();
        this.allowedConfigOrderIds = mergeAccessibleConfigOrderIds(tokenIds, data);
        this.configOrderOptions = filterAccessibleConfigOrders(data, this.allowedConfigOrderIds);
      },
      error: () => {}
    });

    this.statusServiceOrdersService.getAll().subscribe({
      next: (data) => {
        const seen = new Set<string>();
        this.statusOptions = data.filter(s => {
          if (seen.has(s.name)) return false;
          seen.add(s.name);
          return true;
        });
      },
      error: () => {}
    });
  }

  search(): void {
    this.hasSearched = true;
    this.currentPage = 1;
    this.loadServiceOrders();
  }

  loadServiceOrders(): void {
    this.loading = true;
    this.error = '';

    if (this.allowedConfigOrderIds.length === 0) {
      this.serviceOrders = [];
      this.cachedAllOrders = [];
      this.totalCount = 0;
      this.totalPages = 1;
      this.currentPage = 1;
      this.loading = false;
      this.error = 'Nenhum tipo de OS permitido para este usuário.';
      return;
    }

    const baseFilters: any = {};
    if (this.selectedStatus) baseFilters.status = this.selectedStatus;
    if (this.filterOrderNumber?.trim()) baseFilters.orderNumber = this.filterOrderNumber.trim();
    if (this.filterPlate?.trim()) baseFilters.plate = this.filterPlate.trim();
    if (this.filterStartDate) baseFilters.startDate = new Date(this.filterStartDate).toISOString();
    if (this.filterEndDate) baseFilters.endDate = new Date(this.filterEndDate + 'T23:59:59').toISOString();
    const b2bId = Number(this.filterB2bContractId);
    if (Number.isFinite(b2bId) && b2bId > 0) baseFilters.b2bContractId = b2bId;
    if (this.filterIsScheduled === 'true') baseFilters.isScheduled = true;
    if (this.filterIsScheduled === 'false') baseFilters.isScheduled = false;

    if (this.selectedConfigOrderId) {
      if (!this.allowedConfigOrderIds.includes(this.selectedConfigOrderId)) {
        this.serviceOrders = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.loading = false;
        return;
      }
      this.isClientPaginated = false;
      this.cachedAllOrders = [];
      this.fetchServerPaginated({ ...baseFilters, configOrderId: this.selectedConfigOrderId });
    } else if (this.allowedConfigOrderIds.length === 1) {
      this.isClientPaginated = false;
      this.cachedAllOrders = [];
      this.fetchServerPaginated({ ...baseFilters, configOrderId: this.allowedConfigOrderIds[0] });
    } else {
      this.isClientPaginated = true;
      this.fetchAllAndPaginateClientSide(baseFilters);
    }
  }

  private fetchServerPaginated(filters: any): void {
    this.serviceOrdersService.getPaginated(this.currentPage, this.pageSize, filters).subscribe({
      next: (res) => {
        this.serviceOrders = this.transformServiceOrders(res.serviceOrders);
        this.totalCount = res.totalCount;
        this.currentPage = res.page;
        this.pageSize = res.pageSize;
        this.totalPages = Math.max(1, res.totalPages);
        this.resolveDepartmentNames();
        this.loading = false;
      },
      error: () => {
        this.error = 'Erro ao carregar ordens de serviço. Por favor, tente novamente.';
        this.loading = false;
      }
    });
  }

  private fetchAllAndPaginateClientSide(baseFilters: any): void {
    const requests = this.allowedConfigOrderIds.map(id =>
      this.serviceOrdersService
        .getPaginated(1, MAX_ORDERS_PER_CONFIG, { ...baseFilters, configOrderId: id })
        .pipe(
          map(res => res.serviceOrders),
          catchError(() => of([] as ServiceOrder[]))
        )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        const seen = new Set<number>();
        const merged: ServiceOrder[] = [];
        for (const list of results) {
          for (const os of list) {
            if (os.id != null && !seen.has(os.id)) {
              seen.add(os.id);
              merged.push(os);
            }
          }
        }

        merged.sort((a, b) => {
          const da = a.dataAbertura ? new Date(a.dataAbertura).getTime() : 0;
          const db = b.dataAbertura ? new Date(b.dataAbertura).getTime() : 0;
          return db - da;
        });

        this.cachedAllOrders = merged;
        this.totalCount = merged.length;
        this.currentPage = 1;
        this.totalPages = Math.max(1, Math.ceil(merged.length / this.pageSize));
        this.applyClientPagination();
        this.loading = false;
      },
      error: () => {
        this.error = 'Erro ao carregar ordens de serviço. Por favor, tente novamente.';
        this.loading = false;
      }
    });
  }

  private applyClientPagination(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const page = this.cachedAllOrders.slice(start, start + this.pageSize);
    this.serviceOrders = this.transformServiceOrders(page);
    this.resolveDepartmentNames();
  }

  transformServiceOrders(orders: ServiceOrder[]): any[] {
    return orders.map(os => ({
      ...os,
      setorAtual: this.getSetorAtual(os),
      tipoOSNome: os.configOrder?.typeOS || os.tipoOS || os.tipo || 'N/A',
      _actions: this.tableActions
    }));
  }

  getSetorAtual(os: ServiceOrder): string {
    if (os.historico && os.historico.length > 0) {
      const ultimoDirecionamento = [...os.historico]
        .reverse()
        .find(h => h.tipo === 'direcionamento' && h.setorDestino);

      if (ultimoDirecionamento?.setorDestino) {
        return ultimoDirecionamento.setorDestino;
      }
    }

    const deptId = os.unidadeId ?? (os as any).departmentId ?? (os as any).DepartmentId;
    if (deptId && this.departmentNameCache.has(deptId)) {
      return this.departmentNameCache.get(deptId)!;
    }

    return os.setorAbertura || 'Recepção';
  }

  private resolveDepartmentNames(): void {
    const deptIds = new Set<number>();
    this.serviceOrders.forEach(os => {
      const deptId = os.unidadeId ?? (os as any).departmentId ?? (os as any).DepartmentId;
      if (deptId && !this.departmentNameCache.has(deptId)) {
        deptIds.add(deptId);
      }
    });

    if (deptIds.size === 0) return;

    const requests = Array.from(deptIds).map(id =>
      this.departmentService.getDepartmentById(id).pipe(
        map(dept => ({ id, name: dept.name })),
        catchError(() => of({ id, name: '' }))
      )
    );

    forkJoin(requests).subscribe(results => {
      results.forEach(r => {
        if (r.name) this.departmentNameCache.set(r.id, r.name);
      });
      this.serviceOrders = this.serviceOrders.map(os => ({
        ...os,
        setorAtual: this.getSetorAtual(os)
      }));
    });
  }

  clearFilters(): void {
    this.selectedStatus = '';
    this.filterOrderNumber = '';
    this.filterPlate = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.selectedConfigOrderId = null;
    this.filterB2bContractId = '';
    this.filterIsScheduled = '';
    this.currentPage = 1;
    this.hasSearched = false;
    this.serviceOrders = [];
    this.cachedAllOrders = [];
    this.totalCount = 0;
    this.totalPages = 1;
    this.error = '';
    this.isClientPaginated = false;
  }

  hasActiveFilters(): boolean {
    return !!(
      this.selectedStatus ||
      this.filterOrderNumber ||
      this.filterPlate ||
      this.filterStartDate ||
      this.filterEndDate ||
      this.selectedConfigOrderId ||
      this.filterB2bContractId ||
      this.filterIsScheduled
    );
  }

  getConfigOrderName(id: number): string {
    return this.configOrderOptions.find(c => c.id === id)?.typeOS || '';
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    if (this.isClientPaginated) {
      this.applyClientPagination();
    } else {
      this.loadServiceOrders();
    }
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    if (this.isClientPaginated) {
      this.totalPages = Math.max(1, Math.ceil(this.cachedAllOrders.length / this.pageSize));
      this.applyClientPagination();
    } else {
      this.loadServiceOrders();
    }
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.serviceOrders = [...this.serviceOrders].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onTableAction(event: { action: string; item: ServiceOrder }): void {
    if (event.action === 'view') {
      this.openViewModal(event.item);
    }
  }

  onRowClick(item: ServiceOrder): void {
    this.openViewModal(item);
  }

  openViewModal(serviceOrder: ServiceOrder): void {
    if (!serviceOrder?.id) return;
    this.selectedServiceOrderId = serviceOrder.id;
    this.showViewModal = true;
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedServiceOrderId = null;
  }

  private getStatusCellClass(status: any): string {
    const normalized = this.normalizeStatus(status);
    if (!normalized) {
      return 'os-status-cell os-status--neutral';
    }

    if (
      normalized === 'encerrada' ||
      normalized === 'encerrado' ||
      normalized === 'finalizada' ||
      normalized === 'finalizado' ||
      normalized === 'concluida' ||
      normalized === 'concluido'
    ) {
      return 'os-status-cell os-status--success';
    }

    if (
      normalized === 'cancelada' ||
      normalized === 'cancelado' ||
      normalized === 'reprovada' ||
      normalized === 'reprovado'
    ) {
      return 'os-status-cell os-status--danger';
    }

    if (
      normalized.includes('andamento') ||
      normalized.includes('execucao') ||
      normalized.includes('em curso') ||
      normalized.includes('em atendimento')
    ) {
      return 'os-status-cell os-status--info';
    }

    if (
      normalized.includes('aberta') ||
      normalized.includes('aberto') ||
      normalized.includes('pendente') ||
      normalized.includes('aguardando') ||
      normalized.includes('aguarda')
    ) {
      return 'os-status-cell os-status--warning';
    }

    return 'os-status-cell os-status--neutral';
  }

  private normalizeStatus(value: any): string {
    if (value === null || value === undefined) return '';
    const text = String(value).trim().toLowerCase();
    if (!text) return '';
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
}
